import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { compose, withHandlers, withState } from 'recompose';
import { reduxForm, Field, SubmissionError } from 'redux-form';

import { Button } from 'components/core/Button';
import { Form, FormItem, FormGroup, TextInput } from 'components/core/Form';
import { H3, Paragraph } from 'components/core/Typography';
import { IconText } from 'components/core/IconText';
import { Link } from 'components/core/Link';

import { createValidator, required, email, phone } from 'utils/form/validation';

import './hoursAndDirectionsForm.scss';

const validate = createValidator({
	firstName: [required],
	lastName: [required],
	email: [required, email],
	phone: [required, phone]
});

export const enhance = compose(
	withState('directionsSent', 'setDirectionsSent', {}),
	withHandlers({
		onSubmit: (props) => (data) => props.onSubmit({
			...data,
			facilityId: props.facilityId,
			uri: typeof window !== 'undefined' && window.location.href
		}).then(({ error, payload }) => {
			if (error) {
				throw new SubmissionError({ _error: error.message });
			}

			// Removes starting address from map link set in Phacade/Genesis
			// saddr was repeat of faciltiy address
			payload.link.url = get(payload, 'link.url')?.replace(/(saddr=)[^&]*/, '$1');

			props.setDirectionsSent(payload);
		})
	}),
	reduxForm({
		form: 'hours',
		validate
	})
);

export function HoursAndDirectionsForm({
	handleSubmit, onClose, submitSucceeded, directionsSent, error, submitting
}) {
	return (
		<div className="hours-and-directions-form">
			<H3 size="medium">
				Get facility hours and directions
			</H3>
			{!submitSucceeded &&
				<Form
					onSubmit={handleSubmit}
					successLabel="form submit success"
					failureLabel="form submit failure"
					id="hours-and-directions-form"
				>
					{!!error &&
						<IconText type="alert">
							{error}
						</IconText>
					}
					<FormGroup>
						<FormItem>
							<Field
								name="firstName"
								id="firstName"
								label="First name"
								errorMessage="Please provide your first name"
								component={TextInput}
							/>
						</FormItem>
						<FormItem>
							<Field
								name="lastName"
								id="lastName"
								label="Last name"
								errorMessage="Please provide your last name"
								component={TextInput}
							/>
						</FormItem>
					</FormGroup>
					<FormGroup>
						<FormItem>
							<Field
								name="email"
								id="email"
								label="Email address"
								type="email"
								errorMessage="Please provide a valid email address"
								component={TextInput}
							/>
						</FormItem>
						<FormItem>
							<Field
								name="phone"
								id="phone"
								label="Phone number"
								type="text"
								errorMessage="Please provide a valid phone number"
								component={TextInput}
							/>
						</FormItem>
					</FormGroup>
					<FormGroup>
						<FormItem>
							<Button
								type="submit"
								disabled={submitting}
							>
								Email directions
							</Button>
						</FormItem>
					</FormGroup>
					<Paragraph last className="tos">
						By submitting your email address, you are confirming that you have read, understood
						and accept our
						{' '}
						<Link href="/legal/consumer">
							Terms & Conditions
						</Link>.
					</Paragraph>
				</Form>
			}
			{submitSucceeded &&
				<div className="directions-sent">
					<Paragraph>
						{directionsSent.message}
					</Paragraph>
					<div className="directions-sent-actions">
						<Button
							dark
							onClick={onClose}
						>
							Got it, thanks!
						</Button>
						<Button
							href={get(directionsSent, 'link.url')}
							target="_blank"
							rel="noopener noreferrer"
						>
							{get(directionsSent, 'link.text')}
						</Button>
					</div>
				</div>
			}
		</div>
	);
}

HoursAndDirectionsForm.propTypes = {
	onClose: PropTypes.func,
	handleSubmit: PropTypes.func,
	submitSucceeded: PropTypes.bool,
	directionsSent: PropTypes.object,
	error: PropTypes.string,
	submitting: PropTypes.bool
};

export default enhance(HoursAndDirectionsForm);
